import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  CircleDashed,
  Globe,
  CodeXml,
  FileCode2,
  Rocket,
  Target,
  ArrowRight,
  Settings,
  RefreshCw,
} from "lucide-react";

const API_BASE_AFFILIATE = "https://api.sobevenda.com.br/api-proxy/affiliate";
const API_BASE_SOBEPAGES = "https://api.sobevenda.com.br/api-proxy/sobepages";
const API_BASE_SCRIPT = "https://api.sobevenda.com.br/api-proxy/script";

function getToken() {
  return localStorage.getItem("sobevenda_token") || "";
}

async function apiRequest(base, path, options = {}) {
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || "Erro ao carregar dados.");
  }

  return data;
}

// Helper local — pode ser movido para src/utils/array.js
function toArray(data, ...possibleKeys) {
  if (Array.isArray(data)) return data;
  for (const key of possibleKeys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function StepRow({ icon: Icon, title, description, done, to, buttonLabel }) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {title}
            </h3>

            {done ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Concluído
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                <CircleDashed className="w-3.5 h-3.5" />
                Pendente
              </span>
            )}
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <Link
        to={to}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm shrink-0"
      >
        <span className="hidden sm:inline">{buttonLabel}</span>
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export default function ConfiguracaoInicial() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [domains, setDomains] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [pages, setPages] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    setError("");

    try {
      const [googleData, domainsData, scriptsData, pagesData] = await Promise.all([
        apiRequest(API_BASE_AFFILIATE, "/google-ads-accounts", { method: "GET" }).catch(() => []),
        apiRequest(API_BASE_SOBEPAGES, "/domains", { method: "GET" }).catch(() => []),
        apiRequest(API_BASE_SCRIPT, "/list-sobescripts-active", { method: "GET" }).catch(() => []),
        apiRequest(API_BASE_SOBEPAGES, "/pages", { method: "GET" }).catch(() => []),
      ]);

      setGoogleAccounts(toArray(googleData, "accounts", "items", "data"));
      setDomains(toArray(domainsData, "domains", "items", "data"));
      setScripts(toArray(scriptsData, "scripts", "items", "data"));
      setPages(toArray(pagesData, "pages", "items", "data"));
    } catch (e) {
      setError(e?.message || "Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const connectedDomains = useMemo(
    () => domains.filter((d) => d.is_active === true || d.status === "connected"),
    [domains]
  );

  const publishedPages = useMemo(
    () => pages.filter((p) => p.status === "published" && p.public_url),
    [pages]
  );

  const steps = useMemo(
    () => [
      {
        key: "google",
        title: "Conectar Google Ads",
        description: "Conecte sua conta para usar automação de conversões e preparar seus scripts para rastreamento.",
        done: googleAccounts.length > 0,
        to: "/google-ads",
        buttonLabel: googleAccounts.length > 0 ? "Gerenciar" : "Abrir",
        icon: Target,
      },
      {
        key: "domain",
        title: "Conectar domínio",
        description: "Seu domínio será usado para publicar páginas direto pelo SobeVenda.",
        done: connectedDomains.length > 0,
        to: "/domains",
        buttonLabel: connectedDomains.length > 0 ? "Gerenciar" : "Abrir",
        icon: Globe,
      },
      {
        key: "script",
        title: "Criar script",
        description: "O script precisa estar pronto antes de ser vinculado a uma página.",
        done: scripts.length > 0,
        to: "/sobescripts",
        buttonLabel: scripts.length > 0 ? "Gerenciar" : "Abrir",
        icon: CodeXml,
      },
      {
        key: "page",
        title: "Criar página",
        description: "Monte sua página, gere o HTML e salve no SobeVenda para publicação.",
        done: pages.length > 0,
        to: "/sobepages",
        buttonLabel: pages.length > 0 ? "Gerenciar" : "Abrir",
        icon: FileCode2,
      },
      {
        key: "publish",
        title: "Publicar página",
        description: "Vincule domínio e script, publique sua página e gere a URL final.",
        done: publishedPages.length > 0,
        to: "/sobepages",
        buttonLabel: publishedPages.length > 0 ? "Ver publicadas" : "Continuar",
        icon: Rocket,
      },
    ],
    [googleAccounts.length, connectedDomains.length, scripts.length, pages.length, publishedPages.length]
  );

  const completedSteps = steps.filter((step) => step.done).length;
  const progressPercent = Math.round((completedSteps / steps.length) * 100);

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Configuração Inicial
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Configure seu SobeVenda passo a passo para começar a publicar páginas e rastrear conversões.
            </p>
          </div>

          <button
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* MENSAGEM DE ERRO */}
        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
            {error}
          </div>
        ) : null}

        {/* PROGRESSO */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 md:p-5">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Progresso
              </p>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-3xl font-extrabold text-slate-900 dark:text-slate-100">
                  {completedSteps}
                </span>
                <span className="pb-1 text-sm text-slate-500 dark:text-slate-400">
                  de {steps.length} etapas
                </span>
              </div>
            </div>

            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {progressPercent}% concluído
            </p>
          </div>

          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-slate-900 dark:bg-slate-100 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* GRID DE KPIs */}
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Google Ads conectadas
            </p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
              {loading ? "-" : googleAccounts.length}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Domínios conectados
            </p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
              {loading ? "-" : connectedDomains.length}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Scripts ativos
            </p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
              {loading ? "-" : scripts.length}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Páginas publicadas
            </p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
              {loading ? "-" : publishedPages.length}
            </p>
          </div>
        </section>

        {/* ETAPAS — agora linhas dentro de um único card */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 md:px-5">
          {steps.map((step) => (
            <StepRow
              key={step.key}
              icon={step.icon}
              title={step.title}
              description={step.description}
              done={step.done}
              to={step.to}
              buttonLabel={step.buttonLabel}
            />
          ))}
        </section>
      </div>
    </div>
  );
}